import { Texture } from "pixi.js";
import { type Card } from "../model/types.ts";
import { type Highlight } from "../model/highlights.ts";
import { snapshotCard } from "./snapshot.ts";
import { imageCandidates, isExternalSrc, isImagePath, isPdfPath } from "../vault/attachments.ts";
import { blobToDataUrl, decodeImageToCanvas, MAX_TEXTURE_DIM } from "./imageTexture.ts";
import { PdfDocument } from "./pdfDocument.ts";

/**
 * Tier stored for image textures. An image is decoded once at a capped size —
 * there is no higher-resolution source to upgrade to — so we mark it as
 * satisfying any requested tier and never re-rasterize it.
 */
const IMAGE_TIER = Infinity;

interface CacheEntry {
  texture: Texture;
  /** Resolution tier the texture was rasterized at. */
  tier: number;
  /** Card.updatedAt the texture was built from; mismatch => stale. */
  updatedAt: number;
  /** Bytes aproximados en VRAM (RGBA), para el presupuesto del desalojo. */
  bytes: number;
  /** Último acceso, en ms. Una tarjeta visible se consulta en cada frame. */
  lastUsed: number;
}

/**
 * Techo aproximado de VRAM en texturas. Al pasarse se desaloja lo menos usado.
 * Una imagen de 2048² son ~16 MB, así que esto da lugar a unas decenas.
 */
const TEXTURE_BUDGET_BYTES = 256 * 1024 * 1024;

/**
 * Una entrada usada hace menos que esto no se desaloja.
 *
 * Es lo que vuelve seguro el desalojo: las tarjetas a la vista se consultan en
 * cada frame, así que nunca caen en la ventana, y no se puede destruir una
 * textura que un sprite está mostrando en este momento.
 */
const EVICT_GRACE_MS = 1000;

/** Tope de imágenes de markdown guardadas como data URL (ver `dataUrls`). */
const MAX_EMBEDDED_IMAGES = 24;

/**
 * Holds one cached texture per card id. The texture is NOT part of the model —
 * it lives only here, in the render layer (spec §5). Entries are invalidated
 * when the card's content changes (updatedAt) or when a higher resolution tier
 * is needed than what's cached.
 *
 * A small concurrency limit keeps a burst of newly-visible cards from blocking
 * the main thread on synchronous html-to-image work.
 */
export class TextureCache {
  private entries = new Map<string, CacheEntry>();
  private pending = new Set<string>();
  private queue: Array<{ card: Card; tier: number }> = [];
  private active = 0;
  private readonly maxConcurrent = 3;

  /** Total rasterizations performed — surfaced in the HUD to verify §8. */
  rasterCount = 0;

  /** Loads an attachment's bytes from the vault; set by the render orchestrator. */
  loadImage: ((path: string) => Promise<Blob>) | null = null;

  /** Avisa que hay una textura nueva para mostrar (saca al lienzo del reposo). */
  onReady: (() => void) | null = null;

  /** Loads a document's highlights; set by the render orchestrator. */
  loadHighlights: ((path: string) => Promise<Highlight[]>) | null = null;

  /**
   * Resaltados ya leídos, por ruta. El sidecar se lee una vez por documento y
   * no en cada cambio de tier. El caché vive lo que vive la vista, así que
   * volver al canvas después de marcar relee y muestra lo nuevo.
   */
  private highlightsByPath = new Map<string, Promise<Highlight[]>>();

  /** Portadas de PDF ya rasterizadas, por ruta. */
  private coversByPath = new Map<string, Promise<HTMLCanvasElement>>();

  /**
   * Data URLs de las imágenes embebidas en el markdown de las tarjetas, por
   * documento + `src`. Una tarjeta se vuelve a rasterizar al cambiar de tier, y
   * sin esto cada cambio de zoom releería el archivo y lo volvería a pasar a
   * base64. Se acota a mano porque un data URL pesa lo que pesa la imagen: el
   * presupuesto de VRAM no cubre este otro montón de memoria.
   */
  private dataUrls = new Map<string, Promise<string | null>>();

  /** Returns a ready texture for the card if cached & fresh enough, else null. */
  get(card: Card, neededTier: number): Texture | null {
    const e = this.entries.get(card.id);
    if (e && e.updatedAt === card.updatedAt && e.tier >= neededTier) {
      e.lastUsed = performance.now();
      return e.texture;
    }
    return null;
  }

  /** Returns whatever texture we have, even if stale/low-res — for instant display while a fresh one renders. */
  getAny(id: string): Texture | null {
    const e = this.entries.get(id);
    if (!e) return null;
    e.lastUsed = performance.now();
    return e.texture;
  }

  /**
   * Ensure a fresh texture at >= neededTier exists, rasterizing if necessary.
   * Cheap and idempotent to call every frame: it no-ops when already satisfied
   * or already queued. This is the gate that guarantees no per-frame raster.
   */
  request(card: Card, neededTier: number): void {
    if (this.get(card, neededTier)) return;
    if (this.pending.has(card.id)) return;

    // De-dupe / upgrade any queued request for the same card.
    const queued = this.queue.find((q) => q.card.id === card.id);
    if (queued) {
      queued.card = card;
      queued.tier = Math.max(queued.tier, neededTier);
      return;
    }
    this.queue.push({ card, tier: neededTier });
    this.pump();
  }

  invalidate(id: string): void {
    const e = this.entries.get(id);
    if (e) {
      e.texture.destroy(true);
      this.entries.delete(id);
    }
  }

  private pump(): void {
    while (this.active < this.maxConcurrent && this.queue.length > 0) {
      const job = this.queue.shift()!;
      // A fresher request may have superseded this one while queued.
      if (this.get(job.card, job.tier)) continue;
      this.active++;
      this.pending.add(job.card.id);
      void this.run(job.card, job.tier);
    }
  }

  /** Resaltados del documento que embebe la tarjeta (vacío si no embebe uno). */
  private highlightsFor(card: Card): Promise<Highlight[]> {
    const path = card.type === "file" ? card.file : undefined;
    if (!path || !this.loadHighlights) return Promise.resolve([]);
    let pending = this.highlightsByPath.get(path);
    if (!pending) {
      // Un sidecar ausente o ilegible no debe impedir que la tarjeta se dibuje.
      pending = this.loadHighlights(path).catch(() => []);
      this.highlightsByPath.set(path, pending);
    }
    return pending;
  }

  /**
   * Portada de un PDF: la página 1 rasterizada.
   *
   * Se memoriza por ruta para no reabrir el documento cuando el mismo PDF está
   * varias veces en el canvas, y el documento se cierra ni bien se dibujó — el
   * worker de pdf.js no tiene por qué quedar vivo por una portada.
   */
  private pdfCover(path: string, targetWidth: number): Promise<HTMLCanvasElement> {
    let pending = this.coversByPath.get(path);
    if (!pending) {
      pending = (async () => {
        const blob = await this.loadImage!(path);
        const doc = await PdfDocument.open(blob);
        try {
          const { width } = await doc.pageSize(0);
          const scale = Math.min(MAX_TEXTURE_DIM, targetWidth) / Math.max(1, width);
          return (await doc.renderPage(0, scale)).canvas;
        } finally {
          doc.destroy();
        }
      })();
      this.coversByPath.set(path, pending);
    }
    return pending;
  }

  /**
   * El `src` de una imagen del markdown, resuelto a un data URL con sus bytes.
   *
   * Se prueban las mismas rutas que en la vista del documento
   * (`imageCandidates`), así una imagen que se ve al abrir la nota se ve
   * también en su tarjeta del canvas.
   */
  private cardImage(docPath: string, src: string): Promise<string | null> {
    if (isExternalSrc(src)) return Promise.resolve(src);
    const loadImage = this.loadImage;
    if (!loadImage) return Promise.resolve(null);

    // Separador que no puede aparecer en una ruta ni en un `src`, escrito
    // como escape y NO como byte crudo: un NUL literal en el archivo hace
    // que git lo tome por binario y deje de mostrar sus diffs.
    const key = `${docPath}\u0000${src}`;
    let pending = this.dataUrls.get(key);
    if (!pending) {
      pending = (async () => {
        for (const candidate of imageCandidates(docPath, src)) {
          try {
            return await blobToDataUrl(await loadImage(candidate));
          } catch {
            // no está en esta ubicación — se prueba la siguiente
          }
        }
        return null;
      })();
      // FIFO: `Map` conserva el orden de inserción, así que la primera clave es
      // la más vieja. Se descarta antes de insertar para no pasarse del tope.
      if (this.dataUrls.size >= MAX_EMBEDDED_IMAGES) {
        this.dataUrls.delete(this.dataUrls.keys().next().value!);
      }
      this.dataUrls.set(key, pending);
    }
    return pending;
  }

  private async run(card: Card, tier: number): Promise<void> {
    try {
      const path = card.type === "file" ? card.file : undefined;
      let canvas: HTMLCanvasElement;
      let storedTier = tier;

      if (path && isImagePath(path) && this.loadImage) {
        canvas = await decodeImageToCanvas(await this.loadImage(path));
        storedTier = IMAGE_TIER;
      } else if (path && isPdfPath(path) && this.loadImage) {
        // La portada se dibuja a la resolución del tier actual, acotada por el
        // mismo techo que las imágenes.
        canvas = await this.pdfCover(path, card.width * tier);
        storedTier = IMAGE_TIER;
      } else {
        canvas = await snapshotCard(
          card,
          tier,
          await this.highlightsFor(card),
          path ? (src) => this.cardImage(path, src) : undefined,
        );
      }

      const texture = Texture.from(canvas);
      texture.source.scaleMode = "linear";

      const prev = this.entries.get(card.id);
      if (prev) prev.texture.destroy(true);
      this.entries.set(card.id, {
        texture,
        tier: storedTier,
        updatedAt: card.updatedAt,
        bytes: canvas.width * canvas.height * 4,
        lastUsed: performance.now(),
      });
      this.rasterCount++;
      this.evictIfOverBudget();
      this.onReady?.();
    } catch (err) {
      console.error("snapshot failed", card.id, err);
    } finally {
      this.pending.delete(card.id);
      this.active--;
      this.pump();
    }
  }

  /**
   * Libera las texturas menos usadas hasta volver bajo presupuesto.
   *
   * Sin esto las entradas sólo salían por `invalidate()`: nada las sacaba por
   * memoria, y un canvas con muchas imágenes o portadas de PDF crecía sin
   * techo hasta agotar la VRAM.
   */
  private evictIfOverBudget(): void {
    let total = 0;
    for (const e of this.entries.values()) total += e.bytes;
    if (total <= TEXTURE_BUDGET_BYTES) return;

    const now = performance.now();
    const candidates = [...this.entries.entries()]
      .filter(([id, e]) => !this.pending.has(id) && now - e.lastUsed > EVICT_GRACE_MS)
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);

    for (const [id, e] of candidates) {
      if (total <= TEXTURE_BUDGET_BYTES) break;
      e.texture.destroy(true);
      this.entries.delete(id);
      total -= e.bytes;
    }
  }
}
